import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LoadingSpinner from "@/components/LoadingSpinner";
import { trpc } from "@/lib/trpc/client";
import {
  formatDistance,
  formatTimeSeconds,
  type DistanceUnit,
} from "@/lib/utils/exerciseTracking";

type ExerciseDetail = {
  id: string;
  name: string;
  tracking_type?: string | null;
  instructions?: string[] | null;
  primary_muscles?: string[] | null;
  equipment?: string | null;
  experience?: string | null;
};

type ExerciseHistoryGroup = {
  id: string;
  start_time: string;
  name: string;
  sets: {
    set_num: number;
    reps: number | null;
    weight: number | null;
    time_seconds: number | null;
    distance_meters: number | null;
    is_pr: boolean;
  }[];
};

type ExerciseDetailDialogProps = {
  exercise: ExerciseDetail | null;
  clientId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weightUnitLabel: string;
  distanceUnitLabel: DistanceUnit;
  showEditExerciseButton?: boolean;
  onEditExercise?: () => void;
  analyticsName?: string;
};

const getYoutubeVideoId = (url?: string | null) => {
  if (!url) return null;
  const rx =
    /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/;
  const match = url.match(rx);
  return match ? match[1] : null;
};

const ExerciseDetailDialog = ({
  exercise,
  clientId,
  open,
  onOpenChange,
  weightUnitLabel,
  distanceUnitLabel,
  showEditExerciseButton = false,
  onEditExercise,
  analyticsName,
}: ExerciseDetailDialogProps) => {
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);

  const { data: exerciseHistoryData, isLoading: isExerciseHistoryLoading } =
    trpc.workouts.exerciseHistory.useQuery(
      {
        clientId: clientId ?? "",
        exerciseId: exercise?.id ?? "",
        limit: 20,
      },
      { enabled: Boolean(open && exercise && clientId) },
    );
  const exerciseHistory = exerciseHistoryData?.sets;
  const exerciseNotes = exerciseHistoryData?.notes;

  const { data: libraryExercises } = trpc.exercises.list.useQuery(undefined, {
    enabled: Boolean(open && exercise),
  });
  const exerciseVideoUrl =
    libraryExercises?.find((item) => item.id === exercise?.id)?.url ?? null;

  const exerciseVideoId = useMemo(
    () => getYoutubeVideoId(exerciseVideoUrl),
    [exerciseVideoUrl],
  );

  const exerciseNotesByLogId = useMemo(() => {
    const map: Record<string, string> = {};
    (exerciseNotes ?? []).forEach((row) => {
      if (row.log_id && row.notes) {
        map[row.log_id] = row.notes;
      }
    });
    return map;
  }, [exerciseNotes]);

  const exerciseHistoryGroups = useMemo(() => {
    if (!exerciseHistory || !exercise) return [];
    const grouped: ExerciseHistoryGroup[] = [];
    let lastId: string | null = null;

    exerciseHistory.forEach((row) => {
      if (row.workout_log_id !== lastId) {
        grouped.push({
          id: row.workout_log_id ?? String(Math.random()),
          start_time: row.start_time ?? "",
          name: row.workout_name ?? "Workout",
          sets: [],
        });
        lastId = row.workout_log_id ?? null;
      }

      grouped[grouped.length - 1].sets.push({
        set_num: row.set_num ?? 0,
        reps: row.reps ?? null,
        weight: row.weight ?? null,
        time_seconds: row.time_seconds ?? null,
        distance_meters: row.distance_meters ?? null,
        is_pr: Boolean(row.is_pr),
      });
    });

    return grouped;
  }, [exerciseHistory, exercise]);

  const infoItems = [
    ...(exercise?.primary_muscles ?? []),
    exercise?.equipment,
    exercise?.experience,
  ].filter((item): item is string => Boolean(item));

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setInstructionsExpanded(false);
        }
        onOpenChange(nextOpen);
      }}
      analyticsName={analyticsName}
    >
      <DialogContent className="flex max-h-[82vh] max-w-2xl flex-col overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{exercise?.name ?? "Exercise"}</DialogTitle>
          {infoItems.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {infoItems.map((item) => (
                <Badge key={item} variant="secondary" className="capitalize">
                  {item}
                </Badge>
              ))}
            </div>
          ) : null}
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {(exerciseVideoId || exercise?.instructions?.length !== 0) && (
            <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
              {exerciseVideoId && (
                <div className="aspect-video overflow-hidden rounded-md">
                  <iframe
                    title="Exercise video"
                    className="h-full w-full"
                    src={`https://www.youtube.com/embed/${exerciseVideoId}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              {exercise?.instructions?.length ? (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-foreground">
                      Instructions
                    </div>
                    {exercise.instructions.length > 3 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setInstructionsExpanded((prev) => !prev)
                        }
                      >
                        {instructionsExpanded ? "View less" : "View more"}
                      </Button>
                    )}
                  </div>
                  <ol className="list-decimal space-y-1 pl-5">
                    {exercise.instructions
                      .filter((line) => line.trim() !== "")
                      .slice(0, instructionsExpanded ? undefined : 3)
                      .map((line, index) => (
                        <li key={`${exercise.id}-step-${index}`}>{line}</li>
                      ))}
                  </ol>
                </div>
              ) : null}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                History
              </span>
            </div>
            {isExerciseHistoryLoading && (
              <div className="flex items-center justify-center py-6">
                <LoadingSpinner className="size-5" />
              </div>
            )}
            {!isExerciseHistoryLoading && exerciseHistoryGroups.length === 0 && (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No exercise history yet.
              </div>
            )}
            <div className="space-y-4">
              {exerciseHistoryGroups.map((entry) => {
                const trackingType = exercise?.tracking_type ?? "strength";
                return (
                  <div
                    key={`history-${entry.id}`}
                    className="rounded-lg border bg-muted/20 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm font-semibold">{entry.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {entry.start_time
                          ? new Date(entry.start_time).toLocaleString(
                              undefined,
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )
                          : ""}
                      </div>
                    </div>
                    {exerciseNotesByLogId[entry.id] && (
                      <div className="mt-2 rounded-md border border-dashed bg-background px-3 py-2 text-xs text-muted-foreground">
                        {exerciseNotesByLogId[entry.id]}
                      </div>
                    )}
                    <div className="mt-3 space-y-2">
                      {entry.sets.map((set, setIndex) => {
                        let value = "";
                        if (trackingType.startsWith("distance")) {
                          const distanceLabel = formatDistance(
                            set.distance_meters,
                            distanceUnitLabel,
                            trackingType,
                          );
                          const timeLabel = set.time_seconds
                            ? ` in ${formatTimeSeconds(set.time_seconds)}`
                            : "";
                          value = `${distanceLabel}${timeLabel}`;
                        } else if (trackingType === "timed") {
                          value = formatTimeSeconds(set.time_seconds);
                        } else if (trackingType === "strength") {
                          value = `${set.weight ?? 0}${weightUnitLabel} x ${
                            set.reps ?? 0
                          }`;
                        } else {
                          value = `${set.reps ?? 0} reps`;
                        }

                        return (
                          <div
                            key={`history-${entry.id}-${setIndex}`}
                            className="flex items-center gap-3 text-sm"
                          >
                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-background text-xs font-semibold">
                              {set.set_num + 1}
                            </div>
                            <div className="flex-1">{value}</div>
                            {set.is_pr && (
                              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                                PR
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter className="border-t px-6 py-4">
          {showEditExerciseButton && onEditExercise ? (
            <Button variant="outline" onClick={onEditExercise}>
              Edit exercise
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExerciseDetailDialog;
